/**
 * Platform administrator directory (FO-111).
 *
 * Read-only on purpose. Inviting and revoking run through
 * `ops/add-platform-admin.sh` with an operator's own AWS credentials, because
 * the API's runtime role has NO write access to the admin Cognito pool — a
 * fully compromised API still cannot mint a platform admin. Listing needs only
 * reads, and a read cannot escalate, so the visibility is close to free.
 *
 * The column that earns this page is `mfa_enrolled`. It is the exact field the
 * authorisation gate reads, so an admin showing "Not enrolled" here will be
 * refused by every /super-admin route — the failure mode of FO-110, which was
 * invisible precisely because nothing displayed it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, PlatformAdmin, PlatformAdminDirectory } from '../lib/api';
import { ShieldCheck, ShieldAlert, Loader, RefreshCw, Terminal, UserX } from 'lucide-react';

/** Cognito account statuses, in words an operator can act on. */
const STATUS_LABEL: Record<string, string> = {
  FORCE_CHANGE_PASSWORD: "Invited — hasn't set a password",
  CONFIRMED: 'Active',
};

function statusLabel(status: string | null): string {
  if (!status) return '—';
  const known = STATUS_LABEL[status];
  if (known) return known;
  const words = status.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const PlatformAdmins: React.FC = () => {
  const { token } = useAdminAuth();
  const [data, setData] = useState<PlatformAdminDirectory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await new AdminAPI(token).getPlatformAdmins();
      setData(result);
      setError('');
    } catch (err) {
      const why = err instanceof Error ? err.message : '';
      setError(`Couldn't load the administrator list. Retry, or check the API.${why ? ` (${why})` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const unenrolled = (data?.admins ?? []).filter((a) => !a.mfa_enrolled).length;

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform administrators</h1>
          <p className="text-gray-600 mt-1">
            Everyone who can access this portal, and whether their authenticator is actually registered.
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          title="Refresh the list"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => void load()}
            className="shrink-0 px-3 py-1 rounded-lg border border-red-300 bg-white text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !data ? (
        <div className="mt-10 flex items-center text-gray-500">
          <Loader className="w-5 h-5 mr-2 animate-spin" /> Loading administrators…
        </div>
      ) : data ? (
        <>
          {unenrolled > 0 && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>{unenrolled} administrator{unenrolled > 1 ? 's have' : ' has'} no
                authenticator registered.</strong> They can sign in but every action in this
                portal will be refused until enrolment completes.
              </div>
            </div>
          )}

          <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Administrator</th>
                  <th className="text-left font-medium px-4 py-3">Account</th>
                  <th className="text-left font-medium px-4 py-3">Two-factor (MFA)</th>
                  <th className="text-left font-medium px-4 py-3">Added</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.admins.map((a: PlatformAdmin) => (
                  <tr key={a.email ?? Math.random()} className={a.enabled ? '' : 'opacity-60'}>
                    <td className="px-4 py-3">
                      <span className="text-gray-900">{a.email ?? '—'}</span>
                      {a.is_you && <span className="ml-2 text-xs text-gray-500">(you)</span>}
                    </td>
                    <td className="px-4 py-3">
                      {!a.enabled ? (
                        <span className="inline-flex items-center text-gray-500">
                          <UserX className="w-4 h-4 mr-1" /> Disabled
                        </span>
                      ) : (
                        <span className="text-gray-600" title={a.status ?? undefined}>{statusLabel(a.status)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {a.mfa_enrolled ? (
                        <span className="inline-flex items-center text-emerald-700">
                          <ShieldCheck className="w-4 h-4 mr-1" /> Enrolled
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-amber-700">
                          <ShieldAlert className="w-4 h-4 mr-1" /> Not enrolled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
                {data.admins.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                      No administrators found. Invite one with the script below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <Terminal className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-gray-900">Adding and removing administrators is intentionally not possible here.</p>
                <p className="mt-1">
                  This platform's API has no write access to the admin directory, so a
                  compromise of it cannot create an administrator. Provisioning runs with your
                  own AWS credentials:
                </p>
                <pre className="mt-2 p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">
{`./${data.managed_by} new.admin@supragent.ai            # invite
./${data.managed_by} new.admin@supragent.ai --resend   # temp password expired
./${data.managed_by} old.admin@supragent.ai --revoke   # remove access`}
                </pre>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PlatformAdmins;
